// api/animationSettingsAPI.js
import { elakay_API } from "../../config";
import { decryptData } from "../auth/elakayCryptage";

let __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["animationsettingsapi"]?.[key] || fallback;

const API_BASE = `${elakay_API}`;

/**
 * Headers pour les requêtes authentifiées
 */
const getHeaders = (token) => ({
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
});

/**
 * Helper : fetch + decrypt
 */
const fetchAndDecrypt = async (url, options = {}) => {
    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    const encryptedData = await response.json();
    return decryptData(encryptedData);
};

/**
 * resetPageCategory
 *
 * Envoie un DELETE sur /category/:categoryId/page-settings
 * Le serveur remet cartegory.pageCategory à {} et sauvegarde.
 */

export const resetPageCategory = (token, categoryId) => {
    return fetch(`${elakay_API}/category/${categoryId}/page-category/reset`, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    })
        .then(res => res.json())
        .catch(err => ({ error: err.message || 'Erreur réseau' }));
};


/**
 * GET - Récupérer le premier paramètre d'animation 
 */
export const getFirstAnimationSettingsAPI = async () => {
    try {
        const response = await fetch(
            `${elakay_API}/get_First_Animation_Settings/elakay`,
            {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    // Authorization: `Bearer ${token}`
                }
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.error || "Erreur serveur");
        }

        if (result.success && result.data) {
            // 🔓 Déchiffrement centralisé
            const decryptedSettings = await decryptData(result.data);
            return {
                success: true,
                settings: decryptedSettings
            };
        }

        return {
            success: false,
            error: __elakayText("t001", "No settings found")
        };

    } catch (error) {
        console.error("Error loading first animation settings:", error);
        return {
            success: false,
            error: error.message || "Erreur inconnue"
        };
    }
};

/**
 * GET - Récupérer les paramètres d'animation d'un utilisateur
 */
export const getAnimationSettings = async (userId, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}`,
            {
                method: "GET",
                headers: getHeaders(token),
            }
        );
    } catch (error) {
        console.error("Error fetching animation settings:", error);
        throw error;
    }
};

/**
 * GET - Récupérer mes paramètres d'animation (utilisateur connecté)
 */
export const getMyAnimationSettings = async (token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/me`,
            {
                method: "GET",
                headers: getHeaders(token),
            }
        );
    } catch (error) {
        console.error("Error fetching my animation settings:", error);
        throw error;
    }
};

/**
 * POST - Créer de nouveaux paramètres d'animation
 */
export const createAnimationSettings = async (settingsData, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/create`,
            {
                method: "POST",
                headers: getHeaders(token),
                body: JSON.stringify(settingsData),
            }
        );
    } catch (error) {
        console.error("Error creating animation settings:", error);
        throw error;
    }
};

/**
 * PUT - Mettre à jour tous les paramètres d'animation
 */
export const updateAnimationSettings = async (userId, updates, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}`,
            {
                method: "PUT",
                headers: getHeaders(token),
                body: JSON.stringify(updates),
            }
        );
    } catch (error) {
        console.error("Error updating animation settings:", error);
        throw error;
    }
};

/**
 * PATCH - Mettre à jour un paramètre spécifique
 */
export const updateSingleSetting = async (userId, path, value, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}/setting`,
            {
                method: "PATCH",
                headers: getHeaders(token),
                body: JSON.stringify({ path, value }),
            }
        );
    } catch (error) {
        console.error("Error updating single setting:", error);
        throw error;
    }
};

/**
 * PATCH - Mettre à jour plusieurs paramètres (bulk)
 */
export const updateMultipleSettings = async (userId, updates, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}/settings/bulk`,
            {
                method: "PATCH",
                headers: getHeaders(token),
                body: JSON.stringify({ updates }),
            }
        );
    } catch (error) {
        console.error("Error updating multiple settings:", error);
        throw error;
    }
};

/**
 * PUT - Mettre à jour une section spécifique
 */
export const updateSection = async (userId, section, updates, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}/section/${section}`,
            {
                method: "PUT",
                headers: getHeaders(token),
                body: JSON.stringify(updates),
            }
        );
    } catch (error) {
        console.error("Error updating section:", error);
        throw error;
    }
};

/**
 * POST - Appliquer un preset
 */
export const applyPreset = async (userId, presetName, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}/preset`,
            {
                method: "POST",
                headers: getHeaders(token),
                body: JSON.stringify({ presetName }),
            }
        );
    } catch (error) {
        console.error("Error applying preset:", error);
        throw error;
    }
};

/**
 * POST - Réinitialiser les paramètres d'animation
 */
export const resetAnimationSettings = async (userId, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}/reset`,
            {
                method: "POST",
                headers: getHeaders(token),
            }
        );
    } catch (error) {
        console.error("Error resetting animation settings:", error);
        throw error;
    }
};

/**
 * DELETE - Supprimer les paramètres (admin only)
 */
export const deleteAnimationSettings = async (userId, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/user/${userId}`,
            {
                method: "DELETE",
                headers: getHeaders(token),
            }
        );
    } catch (error) {
        console.error("Error deleting animation settings:", error);
        throw error;
    }
};

/**
 * GET - Récupérer les types d'animation (PUBLIC - NON CHIFFRÉ)
 */
export const getAnimationTypes = async () => {
    try {
        const response = await fetch(`${API_BASE}/animation-settings/types`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching animation types:", error);
        throw error;
    }
};

/**
 * GET - Récupérer les presets disponibles (PUBLIC - NON CHIFFRÉ)
 */
export const getPresets = async () => {
    try {
        const response = await fetch(`${API_BASE}/animation-settings/presets`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching presets:", error);
        throw error;
    }
};

/**
 * POST - Copier les paramètres (admin only)
 */
export const copyAnimationSettings = async (fromUserId, toUserId, token) => {
    try {
        return await fetchAndDecrypt(
            `${API_BASE}/animation-settings/copy`,
            {
                method: "POST",
                headers: getHeaders(token),
                body: JSON.stringify({ fromUserId, toUserId }),
            }
        );
    } catch (error) {
        console.error("Error copying animation settings:", error);
        throw error;
    }
};

const animationSettingsAPI = {
    getAnimationSettings,
    getMyAnimationSettings,
    createAnimationSettings,
    updateAnimationSettings,
    updateSingleSetting,
    updateMultipleSettings,
    updateSection,
    applyPreset,
    resetAnimationSettings,
    deleteAnimationSettings,
    getAnimationTypes,
    getPresets,
    copyAnimationSettings,
};

export default animationSettingsAPI;
